import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
   type Project,
   type Scene,
   type Character,
   type Location,
   type Plot,
} from '../../types/entities';
import { fetchByType, DocumentType } from '../../database';
import type { WorkspaceStatus } from '../../flux/stores/AuthStore';
import SyncStatusBadge from './SyncStatusBadge';
import UserMenu from './UserMenu';

interface EntityCounts {
   scenes: number;
   characters: number;
   locations: number;
   plots: number;
}

interface ProjectsDashboardProps {
   projects: Project[];
   userId: string | null;
   onSelectProject: (projectId: string) => void;
   onNewProject: () => void;
   onEditProject: (projectId: string) => void;
   onDeleteProject: (projectId: string) => void;
   isLoading?: boolean;
   workspaceStatus: WorkspaceStatus;
   isOnline: boolean;
   exportStatus: 'idle' | 'exporting' | 'success' | 'error';
   isSyncBlocked?: boolean;
   syncBlockedMessage?: string | null;
   isSyncing?: boolean;
   menuRef: React.RefObject<HTMLDivElement>;
   menuOpen: boolean;
   onMenuToggle: () => void;
   onMenuClose: () => void;
   onImport: () => void;
   isImporting: boolean;
   showTitlebarInset: boolean;
}

const DEFAULT_COUNTS: EntityCounts = {
   scenes: 0,
   characters: 0,
   locations: 0,
   plots: 0,
};

function ProjectsDashboard({
   projects,
   userId,
   onSelectProject,
   onNewProject,
   onEditProject,
   onDeleteProject,
   isLoading = false,
   workspaceStatus,
   isOnline,
   exportStatus,
   isSyncBlocked,
   syncBlockedMessage,
   isSyncing,
   menuRef,
   menuOpen,
   onMenuToggle,
   onMenuClose,
   onImport,
   isImporting,
   showTitlebarInset,
}: ProjectsDashboardProps) {
   const [countsByProject, setCountsByProject] = useState<Record<string, EntityCounts>>({});
   const [openMenuProjectId, setOpenMenuProjectId] = useState<string | null>(null);
   const menuContainerRef = useRef<HTMLDivElement | null>(null);

   useEffect(() => {
      let cancelled = false;

      const loadCounts = async () => {
         if (!userId || projects.length === 0) {
            setCountsByProject({});
            return;
         }

         try {
            const [scenes, characters, locations, plots] = await Promise.all([
               fetchByType<Scene>(DocumentType.SCENE, userId),
               fetchByType<Character>(DocumentType.CHARACTER, userId),
               fetchByType<Location>(DocumentType.LOCATION, userId),
               fetchByType<Plot>(DocumentType.PLOT, userId),
            ]);

            if (cancelled) return;

            const next: Record<string, EntityCounts> = {};
            for (const project of projects) {
               if (project.id) {
                  next[project.id] = { ...DEFAULT_COUNTS };
               }
            }

            for (const scene of scenes) {
               if (scene.projectId && next[scene.projectId]) {
                  next[scene.projectId].scenes += 1;
               }
            }

            for (const character of characters) {
               if (character.projectId && next[character.projectId]) {
                  next[character.projectId].characters += 1;
               }
            }

            for (const location of locations) {
               if (location.projectId && next[location.projectId]) {
                  next[location.projectId].locations += 1;
               }
            }

            for (const plot of plots) {
               if (plot.projectId && next[plot.projectId]) {
                  next[plot.projectId].plots += 1;
               }
            }

            setCountsByProject(next);
         } catch (error) {
            console.error('Failed to load project metrics:', error);
         }
      };

      loadCounts();

      return () => {
         cancelled = true;
      };
   }, [projects, userId]);

   useEffect(() => {
      const handleClickAway = (event: MouseEvent) => {
         if (!menuContainerRef.current) return;
         if (!menuContainerRef.current.contains(event.target as Node)) {
            setOpenMenuProjectId(null);
         }
      };

      document.addEventListener('mousedown', handleClickAway);
      return () => document.removeEventListener('mousedown', handleClickAway);
   }, []);

   const handleDelete = (projectId: string) => {
      setOpenMenuProjectId(null);
      onDeleteProject(projectId);
   };

   return (
      <div
         className={`flex-1 overflow-y-auto p-6 md:p-8 ${
            showTitlebarInset ? 'pt-14 md:pt-14' : ''
         }`}
      >
         <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8 gap-4">
               <div className="flex items-center gap-3">
                  <svg
                     className="w-7 h-7 text-silver"
                     fill="none"
                     stroke="currentColor"
                     viewBox="0 0 24 24"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M3 7h18M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2m-9 4h.01M12 11h.01M15 11h.01M9 15h.01M12 15h.01M15 15h.01M5 20h14a2 2 0 002-2V7H3v11a2 2 0 002 2z"
                     />
                  </svg>
                  <h1 className="text-3xl font-display text-pearl tracking-wide">Projects</h1>
               </div>

               <div className="flex items-center gap-2">
                  <SyncStatusBadge
                     workspaceStatus={workspaceStatus}
                     isOnline={isOnline}
                     exportStatus={exportStatus}
                     isSyncBlocked={isSyncBlocked}
                     syncBlockedMessage={syncBlockedMessage}
                     isSyncing={isSyncing}
                  />
                  <UserMenu
                     menuRef={menuRef}
                     menuOpen={menuOpen}
                     onToggle={onMenuToggle}
                     onClose={onMenuClose}
                     selectedProjectId={null}
                     onExport={() => undefined}
                     onImport={onImport}
                     isImporting={isImporting}
                  />
                  <button
                     onClick={onNewProject}
                     className="no-drag inline-flex items-center gap-2 rounded-lg bg-amethyst text-ink px-4 py-2 font-mono text-sm hover:bg-amethyst-dark transition-colors"
                  >
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M12 4v16m8-8H4"
                        />
                     </svg>
                     New Project
                  </button>
               </div>
            </div>

            {isLoading ? (
               <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {[0, 1, 2].map((i) => (
                     <div
                        key={i}
                        className="rounded-xl border border-charcoal bg-midnight/70 p-5 animate-pulse"
                     >
                        <div className="h-6 bg-charcoal rounded w-2/3 mb-3" />
                        <div className="h-4 bg-charcoal rounded w-1/4 mb-4" />
                        <div className="space-y-2 mb-6">
                           <div className="h-3 bg-charcoal rounded w-full" />
                           <div className="h-3 bg-charcoal rounded w-5/6" />
                           <div className="h-3 bg-charcoal rounded w-4/6" />
                        </div>
                        <div className="pt-4 border-t border-charcoal flex gap-2">
                           <div className="h-5 bg-charcoal rounded w-16" />
                           <div className="h-5 bg-charcoal rounded w-20" />
                           <div className="h-5 bg-charcoal rounded w-16" />
                        </div>
                     </div>
                  ))}
               </div>
            ) : projects.length === 0 ? (
               <div className="rounded-xl border border-charcoal bg-midnight/60 p-8 text-center">
                  <p className="text-ash font-mono text-sm mb-4">No projects yet</p>
                  <button
                     onClick={onNewProject}
                     className="font-mono text-sm text-amethyst hover:text-amethyst-dark transition-colors"
                  >
                     Create your first project
                  </button>
               </div>
            ) : (
               <div
                  ref={menuContainerRef}
                  className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
               >
                  {projects.map((project, index) => {
                     if (!project.id) return null;
                     const counts = countsByProject[project.id] || DEFAULT_COUNTS;
                     const menuOpen = openMenuProjectId === project.id;

                     return (
                        <motion.button
                           key={project.id}
                           initial={{ opacity: 0, y: 14 }}
                           animate={{ opacity: 1, y: 0 }}
                           transition={{ delay: index * 0.04 }}
                           onClick={() => onSelectProject(project.id as string)}
                           className="text-left group relative rounded-xl border border-charcoal bg-midnight/70 hover:bg-charcoal/70 hover:border-slate transition-colors p-5"
                        >
                           <div className="flex items-start justify-between gap-3 mb-2">
                              <h2 className="text-xl font-display text-pearl leading-tight line-clamp-2">
                                 {project.title || 'Untitled Project'}
                              </h2>

                              <div className="relative">
                                 <button
                                    onClick={(event) => {
                                       event.stopPropagation();
                                       setOpenMenuProjectId(menuOpen ? null : project.id || null);
                                    }}
                                    className="no-drag rounded-md p-1.5 hover:bg-slate transition-colors"
                                    aria-label={`Open actions for ${project.title || 'project'}`}
                                 >
                                    <svg
                                       className="w-5 h-5 text-silver"
                                       fill="currentColor"
                                       viewBox="0 0 20 20"
                                    >
                                       <path d="M10 6a1.75 1.75 0 110-3.5A1.75 1.75 0 0110 6zm0 5.75a1.75 1.75 0 110-3.5 1.75 1.75 0 010 3.5zm0 5.75a1.75 1.75 0 110-3.5A1.75 1.75 0 0110 17.5z" />
                                    </svg>
                                 </button>

                                 {menuOpen && (
                                    <div className="absolute right-0 mt-1 w-36 rounded-lg border border-charcoal bg-midnight shadow-xl z-20 overflow-hidden">
                                       <button
                                          onClick={(event) => {
                                             event.stopPropagation();
                                             setOpenMenuProjectId(null);
                                             onEditProject(project.id as string);
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs font-mono text-silver hover:bg-charcoal transition-colors"
                                       >
                                          Edit project
                                       </button>
                                       <button
                                          onClick={(event) => {
                                             event.stopPropagation();
                                             handleDelete(project.id as string);
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs font-mono text-rose hover:bg-blood/20 transition-colors"
                                       >
                                          Delete project
                                       </button>
                                    </div>
                                 )}
                              </div>
                           </div>

                           {project.genre && (
                              <p className="inline-flex mb-3 text-xs font-mono text-amethyst bg-amethyst/10 border border-amethyst/25 rounded-full px-2 py-1">
                                 {project.genre}
                              </p>
                           )}

                           <p className="text-sm font-mono text-silver/90 line-clamp-3 min-h-[60px]">
                              {project.description ||
                                 'Open this project to start writing scenes, characters, locations, and plot threads.'}
                           </p>

                           <div className="mt-5 pt-4 border-t border-charcoal flex flex-wrap gap-2">
                              <span className="text-xs font-mono text-ash rounded-md bg-ink/70 px-2 py-1">
                                 {counts.scenes} scenes
                              </span>
                              <span className="text-xs font-mono text-ash rounded-md bg-ink/70 px-2 py-1">
                                 {counts.characters} characters
                              </span>
                              <span className="text-xs font-mono text-ash rounded-md bg-ink/70 px-2 py-1">
                                 {counts.locations} locations
                              </span>
                              <span className="text-xs font-mono text-ash rounded-md bg-ink/70 px-2 py-1">
                                 {counts.plots} plots
                              </span>
                           </div>
                        </motion.button>
                     );
                  })}
               </div>
            )}
         </div>
      </div>
   );
}

export default ProjectsDashboard;
